import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/data';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminStats.css';

const AdminStats = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchUsers();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Ошибка загрузки пользователей');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserStats = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStats(response.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setError('Ошибка загрузки статистики пользователя');
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    fetchUserStats(user.id);
  };

  const formatCurrency = (amount, currency = 'RUB') => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('ru-RU').format(number || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="admin-stats">
        <div className="admin-header">
          <div className="admin-header__logo">
            <img src="/assets/img/logo.svg" alt="Pro Investor" />
          </div>
          <div className="admin-header__title">Статистика</div>
          <div className="admin-header__actions">
            <button className="admin-header__back" onClick={() => navigate('/admin')}>
              <span className="--icon-arrow"></span>
              Назад
            </button>
            <button className="admin-header__logout" onClick={() => logoutAdmin()}>
              <span className="--icon-log-out"></span>
              Выйти
            </button>
          </div>
        </div>
        <div className="admin-loading">
          <div className="admin-loading__spinner"></div>
          <p>Загрузка пользователей...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-stats">
        <div className="admin-header">
          <div className="admin-header__logo">
            <img src="/assets/img/logo.svg" alt="Pro Investor" />
          </div>
          <div className="admin-header__title">Статистика</div>
          <div className="admin-header__actions">
            <button className="admin-header__back" onClick={() => navigate('/admin')}>
              <span className="--icon-arrow"></span>
              Назад
            </button>
            <button className="admin-header__logout" onClick={() => logoutAdmin()}>
              <span className="--icon-log-out"></span>
              Выйти
            </button>
          </div>
        </div>
        <div className="admin-error">
          <p>{error}</p>
          <button onClick={fetchUsers} className="admin-btn admin-btn--primary">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-stats">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Статистика</div>
        <div className="admin-header__actions">
          <button className="admin-header__back" onClick={() => navigate('/admin')}>
            <span className="--icon-arrow"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={() => logoutAdmin()}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-stats__container">
          <div className="admin-stats__sidebar">
            <h2 className="admin-stats__sidebar-title">Пользователи</h2>
            <div className="admin-stats__user-list">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`admin-stats__user-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="admin-stats__user-info">
                    <div className="admin-stats__user-name">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="admin-stats__user-email">{user.email}</div>
                    <div className="admin-stats__user-role">
                      {user.role || 'Пользователь'}
                    </div>
                  </div>
                  <div className={`admin-stats__user-status ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Активен' : 'Заблокирован'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-stats__main">
            {selectedUser && userStats ? (
              <div className="admin-stats__user-details">
                <div className="admin-stats__user-header">
                  <h1 className="admin-stats__user-title">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </h1>
                  <div className="admin-stats__user-meta">
                    <span className="admin-stats__user-email">{selectedUser.email}</span>
                    <span className={`admin-stats__user-status ${selectedUser.is_active ? 'active' : 'inactive'}`}>
                      {selectedUser.is_active ? 'Активен' : 'Заблокирован'}
                    </span>
                  </div>
                </div>

                <div className="admin-stats__grid">
                  {/* Банковские счета */}
                  <div className="admin-stats__card">
                    <h3 className="admin-stats__card-title">Банковские счета</h3>
                    <div className="admin-stats__card-content">
                      <div className="admin-stats__card-number">
                        {formatNumber(userStats.accounts?.length || 0)}
                      </div>
                      <div className="admin-stats__card-label">Всего счетов</div>
                      
                    </div>
                  </div>

                  {/* Торговые счета */}
                  <div className="admin-stats__card">
                    <h3 className="admin-stats__card-title">Торговые счета</h3>
                    <div className="admin-stats__card-content">
                      <div className="admin-stats__card-number">
                        {formatNumber(userStats.tradingAccounts?.length || 0)}
                      </div>
                      <div className="admin-stats__card-label">Всего счетов</div>
                      {userStats.tradingAccounts && userStats.tradingAccounts.length > 0 && (
                        <div className="admin-stats__card-details">
                          <div className="admin-stats__card-detail">
                            Общий баланс: {formatCurrency(
                              userStats.tradingAccounts.reduce((sum, acc) => sum + parseFloat(acc.balance || 0), 0)
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Операции */}
                  <div className="admin-stats__card">
                    <h3 className="admin-stats__card-title">Операции</h3>
                    <div className="admin-stats__card-content">
                      <div className="admin-stats__card-number">
                        {formatNumber(userStats.operations?.length || 0)}
                      </div>
                      <div className="admin-stats__card-label">За последние 30 дней</div>
                      {userStats.operations && userStats.operations.length > 0 && (
                        <div className="admin-stats__card-details">
                          <div className="admin-stats__card-detail">
                            Пополнений: {userStats.operations.filter(op => op.operation_type === 'deposit').length}
                          </div>
                          <div className="admin-stats__card-detail">
                            Выводов: {userStats.operations.filter(op => op.operation_type === 'withdrawal').length}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Паспортные данные */}
                  <div className="admin-stats__card">
                    <h3 className="admin-stats__card-title">Паспортные данные</h3>
                    <div className="admin-stats__card-content">
                      {userStats.passport ? (
                        <div className="admin-stats__card-details">
                          <div className="admin-stats__card-detail">
                            Серия: {userStats.passport.series || 'Не указана'}
                          </div>
                          <div className="admin-stats__card-detail">
                            Номер: {userStats.passport.number || 'Не указан'}
                          </div>
                          <div className="admin-stats__card-detail">
                            Дата рождения: {userStats.passport.birth_date ? formatDate(userStats.passport.birth_date) : 'Не указана'}
                          </div>
                        </div>
                      ) : (
                        <div className="admin-stats__card-detail">Не заполнены</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Последние операции */}
                {userStats.operations && userStats.operations.length > 0 && (
                  <div className="admin-stats__operations">
                    <h3 className="admin-stats__operations-title">Последние операции</h3>
                    <div className="admin-stats__operations-list">
                      {userStats.operations.slice(0, 10).map((operation) => (
                        <div key={operation.id} className="admin-stats__operation-item">
                          <div className="admin-stats__operation-type">
                            {operation.operation_type === 'deposit' ? 'Пополнение' : 'Вывод'}
                          </div>
                          <div className="admin-stats__operation-amount">
                            {formatCurrency(operation.amount, operation.currency)}
                          </div>
                          <div className="admin-stats__operation-status">
                            {operation.status}
                          </div>
                          <div className="admin-stats__operation-date">
                            {formatDate(operation.created_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="admin-stats__empty">
                <h2>Выберите пользователя</h2>
                <p>Выберите пользователя из списка слева для просмотра его статистики</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
